import React, { useEffect, useRef } from 'react';

import { connect } from 'react-redux';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';

import { Loading, PageHeader } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import PermissionGate from '../../components/PermissionGate/PermissionGate';

import useComponentState from '../../utils/useComponentState';
import { usePermissions } from '../../hooks/usePermissions';
import { getAllSmsTemplates, updateSmsTemplate } from '../../api/SmsTemplates';
import { SMS_PERMISSIONS } from '../../constants/permissions';
import {
  PLACEHOLDER_LABELS,
  getRequiredPlaceholders,
  convertTokensToLabels,
  convertLabelsToTokens,
  getPreviewMessage,
  buildPlaceholderInsertion,
  removePlaceholderLabel,
  validateEditForm,
} from './SmsTemplatesConstants';

import styles from './SmsTemplates.module.scss';

const renderHighlightedTemplate = (displayValue) => {
  const labels = Object.values(PLACEHOLDER_LABELS);
  let remaining = displayValue || '';
  const parts = [];
  let key = 0;

  while (remaining.length > 0) {
    let earliestIndex = -1;
    let matchedLabel = null;

    labels.forEach((label) => {
      const idx = remaining.indexOf(label);
      if (idx !== -1 && (earliestIndex === -1 || idx < earliestIndex)) {
        earliestIndex = idx;
        matchedLabel = label;
      }
    });

    if (earliestIndex === -1) {
      parts.push(remaining);
      break;
    }

    if (earliestIndex > 0) {
      parts.push(remaining.slice(0, earliestIndex));
    }
    parts.push(
      <span key={key++} className={styles.placeholder_tag}>{matchedLabel}</span>
    );
    remaining = remaining.slice(earliestIndex + matchedLabel.length);
  }

  return parts;
};

const SmsTemplates = (props) => {
  const { auth: { token } } = props.auth;
  const { can } = usePermissions();
  const textareaRef = useRef(null);

  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['ID', 'Domain Key', 'Template Value'],
    },
    templatesData: [],
    loadingError: '',
    loading: false,

    showDrawer: false,
    drawerData: null,

    editMode: false,
    editForm: {
      domainKey: '',
      value: '',
    },
    fieldErrors: {},
    editSaving: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setState({ loading: true, loadingError: '' });
    try {
      const response = await getAllSmsTemplates(token);
      setState({ templatesData: Array.isArray(response) ? response : [] });
    } catch {
      setState({ loadingError: 'Oops, something went wrong. Please try again.' });
    }
    setState({ loading: false });
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;
    setState({
      showDrawer: true,
      drawerData: rowData,
      editMode: false,
      editForm: {
        domainKey: rowData.domainKey || '',
        value: convertTokensToLabels(rowData.value || ''),
      },
      fieldErrors: {},
    });
  };

  const handleCloseDrawer = () => {
    setState({ showDrawer: false, drawerData: null, editMode: false });
  };

  const handleInputChange = (field, value) => {
    setState({
      editForm: { ...state.editForm, [field]: value },
      fieldErrors: { ...state.fieldErrors, [field]: '' },
    });
  };

  const handleInsertPlaceholder = (token) => {
    const label = PLACEHOLDER_LABELS[token];
    const textarea = textareaRef.current;
    const currentValue = state.editForm.value;

    if (!textarea) {
      const { newValue } = buildPlaceholderInsertion(currentValue, currentValue.length, currentValue.length, label);
      handleInputChange('value', newValue);
      return;
    }

    const { selectionStart: start, selectionEnd: end } = textarea;
    const { newValue, cursorPos } = buildPlaceholderInsertion(currentValue, start, end, label);
    handleInputChange('value', newValue);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const handleRemovePlaceholderLabel = (label) => {
    handleInputChange('value', removePlaceholderLabel(state.editForm.value, label));
  };

  const handleSave = async () => {
    if (!can(SMS_PERMISSIONS.SMS_TEMPLATES.UPDATE)) {
      toast.error('You are not authorized to perform this action.');
      return;
    }
    const { isValid, errors } = validateEditForm(state.editForm);
    if (!isValid) {
      setState({ fieldErrors: errors });
      return;
    }

    const { editForm, drawerData } = state;
    if (!drawerData) return;

    setState({ editSaving: true });

    try {
      const payload = {
        domainKey: editForm.domainKey,
        value: convertLabelsToTokens(editForm.value).trim(),
      };

      const updated = await updateSmsTemplate(payload, token);

      const updatedRecord = { ...drawerData, ...updated };

      setState({
        drawerData: updatedRecord,
        editMode: false,
        editSaving: false,
        templatesData: state.templatesData.map((t) =>
          t.domainKey === drawerData.domainKey ? updatedRecord : t
        ),
      });

      toast.success('SMS template updated successfully.');
    } catch {
      setState({ editSaving: false });
      toast.error('Failed to update SMS template. Please try again.');
    }
  };

  const renderPlaceholderChips = (domainKey, currentValue) => {
    const required = getRequiredPlaceholders(domainKey);
    if (required.length === 0) return null;

    return (
      <div className={styles.placeholder_row}>
        {required.map((token) => {
          const label = PLACEHOLDER_LABELS[token];
          const present = currentValue.includes(label);
          return (
            <button
              key={token}
              type='button'
              className={`${styles.placeholder_chip} ${present ? styles.placeholder_chip_present : styles.placeholder_chip_missing}`}
              onClick={() => (present ? handleRemovePlaceholderLabel(label) : handleInsertPlaceholder(token))}
              title={present ? 'Click to remove from the message' : 'Required — click to insert'}
            >
              {label} {present ? '✕' : ''}
            </button>
          );
        })}
      </div>
    );
  };

  const renderDrawer = () => {
    const data = state.drawerData;
    if (!data) return null;

    const valueError = state.fieldErrors?.value;

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>
              {!state.editMode ? 'SMS Template Details' : 'Edit SMS Template'}
            </Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            {!state.editMode ? (
              <>
                <div className={styles.detail_row}>
                  <strong>Domain Key:</strong>
                  <span>{data.domainKey || 'N/A'}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Template Value:</strong>
                  <span>{renderHighlightedTemplate(convertTokensToLabels(data.value))}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Preview:</strong>
                  <span className={styles.preview_text}>
                    {getPreviewMessage(convertTokensToLabels(data.value))}
                  </span>
                </div>
              </>
            ) : (
              <div className={styles.edit_form}>
                <label className={styles.form_label}>
                  Domain Key
                  <input
                    type='text'
                    value={state.editForm.domainKey}
                    disabled
                    className={styles.input_disabled}
                  />
                </label>

                <label className={styles.form_label}>
                  Template Value *
                  {renderPlaceholderChips(state.editForm.domainKey, state.editForm.value)}
                  <textarea
                    ref={textareaRef}
                    value={state.editForm.value}
                    onChange={(e) => handleInputChange('value', e.target.value)}
                    placeholder='Enter the SMS template text...'
                    rows={6}
                    className={valueError ? styles.input_error : ''}
                  />
                  {valueError && (
                    <span className={styles.error_message}>{valueError}</span>
                  )}
                  <span className={styles.field_hint}>
                    Click a field above to insert it at the cursor, or click it again to remove it from the message.
                  </span>
                </label>

                <div className={styles.detail_row}>
                  <strong>Message:</strong>
                  <span>{renderHighlightedTemplate(state.editForm.value)}</span>
                </div>

                <div className={styles.detail_row}>
                  <strong>Preview:</strong>
                  <span className={styles.preview_text}>{getPreviewMessage(state.editForm.value)}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.drawer_actions}>
            {!state.editMode ? (
              <PermissionGate permission={SMS_PERMISSIONS.SMS_TEMPLATES.UPDATE}>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={() => setState({ editMode: true })}
                >
                  Edit
                </button>
              </PermissionGate>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setState({ editMode: false, fieldErrors: {} })}
                  disabled={state.editSaving}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleSave}
                  disabled={state.editSaving}
                >
                  {state.editSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </Drawer.Body>
      </>
    );
  };

  const { tableHeading, templatesData, loadingError, loading } = state;

  return (
    <div className={styles.root_div}>
      {loading ? (
        <Loading text='Loading' />
      ) : (
        <div>
          <PageHeader text='SMS Templates' />
          {loadingError ? (
            <p>{loadingError}</p>
          ) : (
            <div className={styles.table_container}>
              <DataTable
                tableData={templatesData}
                tableheading={tableHeading}
                handleOnClick={handleDrawerOpen}
                tag='sms-templates'
                fetchError=''
                searchFilterLoading={false}
              />
            </div>
          )}
        </div>
      )}

      <Drawer show={state.showDrawer} onHide={handleCloseDrawer} placement='right'>
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(SmsTemplates);
